import React from 'react';
import { motion } from 'framer-motion';
import { Users, Target, CheckCircle, XCircle } from 'lucide-react';
export function SocialCooperationPage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-1988" data-mp="39atunssu77mg">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-1989" data-mp="3e6966e3dgrfp">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-1990" data-mp="3j8hz5ag8vtht">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-1991" data-mp="tz2p8dkv7n5">
            <span className="w-8 h-px bg-teal" data-id="element-1992" data-mp="5rr18l8q9s0b"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-1993" data-mp="5r8x72ftq9hu">
              Capability
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-1994" data-mp="fq7x9wmiwwt8"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-1995" data-mp="fqg8zl6wtw2u">
            Social Cooperation
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-1996" data-mp="l2vmsnmvneaa">
            At SyNeural, social cooperation refers to the ability to work
            constructively with other people, navigate disagreement, interpret
            social dynamics accurately, and contribute to shared goals without
            unnecessary conflict, chaos, or withdrawal.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1997" data-mp="13whm1w8ndhph">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1998" data-mp="z254jrqqeggd">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-1999" data-mp="1mrzfg3rhpuxz">
            It includes not only interpersonal skill, but the broader capacity
            to function within groups, teams, communities, and institutions in
            ways that build trust rather than erode it.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-2000" data-mp="37b1ii9x7zua4">
            This capability matters because no serious society, institution, or
            organization can function well if too many people are unable to
            cooperate. Even highly intelligent individuals can weaken systems if
            they are chronically reactive, combative, distrustful,
            status-obsessed, or unable to coordinate with others. Social
            cooperation is one of the core capacities that allows human ability
            to scale into functioning institutions and communities.
          </p>
        </motion.div>
      </section>

      {/* Why It Matters */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-2001" data-mp="rj481qecln2i">
        <div className="max-w-4xl mx-auto" data-id="element-2002" data-mp="2c39bqw77d37r">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-2003" data-mp="2c0523jayxf9p">
            <div className="flex items-center gap-4 mb-6" data-id="element-2004" data-mp="2urxt586h8qes">
              <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-2005" data-mp="2uuk0b6pgh3wh">
                <Target size={24} data-id="element-2006" data-mp="2qtgnkt2pfnzh" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-2007" data-mp="34pkcc6ut744k">
                Why It Matters
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-2008" data-mp="2zpz7xgwve3kg">
              Social cooperation influences whether people can handle conflict
              productively, participate in teams, build trust, and remain
              effective in shared environments. Weak cooperation leads to
              fragmentation, conflict escalation, alienation, and institutional
              dysfunction. Strong cooperation supports healthier communities,
              better teams, more stable organizations, and stronger civic
              participation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Strong Social Cooperation Looks Like */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-2009" data-mp="o69t7hspzq27">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-2010" data-mp="t4be889phzgb">
          <div className="flex items-center gap-4 mb-6" data-id="element-2011" data-mp="5dfpxl4glmdt">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-2012" data-mp="acijo9ezo66h">
              <CheckCircle size={24} data-id="element-2013" data-mp="2nntokg506u30" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-2014" data-mp="2nl7iym1oaonf">
              What Strong Social Cooperation Looks Like
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-2015" data-mp="2elc4o8ctmdj6">
            People with strong social cooperation can disagree without
            immediately collapsing into hostility or withdrawal. They are able
            to listen, negotiate, coordinate, and contribute within group
            settings. They can recognize the difference between necessary
            disagreement and destructive conflict. They also tend to understand
            that shared systems require mutual responsibility, not just
            individual preference.
          </p>
        </motion.div>
      </section>

      {/* What Weak Social Cooperation Looks Like */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-y border-line" data-id="element-2016" data-mp="1gyv855bww3yh">
        <div className="max-w-4xl mx-auto" data-id="element-2017" data-mp="1h17pbg9uiaah">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-2018" data-mp="17aznp0clhpud">
            <div className="flex items-center gap-4 mb-6" data-id="element-2019" data-mp="12e907rygnryx">
              <div className="w-12 h-12 rounded-lg bg-navy-light flex items-center justify-center text-white" data-id="element-2020" data-mp="1q2s7c45rxypf">
                <XCircle size={24} data-id="element-2021" data-mp="1uzioyvx892cy" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-2022" data-mp="1h9ox04qfe1z0">
                What Weak Social Cooperation Looks Like
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-2023" data-mp="1l8xrjbtai3vm">
              Weak social cooperation may appear as chronic conflict, inability
              to handle dissent, social volatility, distrust, rigid ego-defense,
              poor group awareness, or withdrawal from shared responsibility. In
              practical terms, this can damage teams, weaken institutions,
              reduce local trust, and make civic life more unstable and
              fragmented.
            </p>
          </motion.div>
        </div>
      </section>

      {/* How It Can Be Strengthened */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-2024" data-mp="73ulidhn4jdt">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-2025" data-mp="3fjxlry6fb5s4">
          <div className="flex items-center gap-4 mb-6" data-id="element-2026" data-mp="3kge6sjibt40z">
            <div className="w-12 h-12 rounded-lg bg-green-soft flex items-center justify-center text-white" data-id="element-2027" data-mp="14qbifhmgouz1">
              <Users size={24} data-id="element-2028" data-mp="zsjetqcgfkf0" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-2029" data-mp="ztujdsdq4w76">
              How It Can Be Strengthened
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-2030" data-mp="2y8sasjwmgyu3">
            Social cooperation can be developed through conflict-resolution
            training, communication practice, teamwork structures, mentorship,
            civic engagement, emotional regulation work, and environments that
            reward constructive contribution instead of performative hostility.
            In the SyNeural framework, social cooperation is essential because
            strong judgment alone is not enough if a person cannot function well
            with others inside shared systems.
          </p>
        </motion.div>
      </section>
    </div>;
}